/** Abrechnungen (Screen mieter/Abrechnungen): laufende Periode „bisher“, Abrechnungen mit Status und PDF, Mietrechnungen. */
import { useTx } from "../i18n";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { EmptyState, Seitenzustand, StatusBadge, Total, Notice } from "../ui";
import { ItemRow } from "../ui/ItemRow";
import { Card, Dl } from "../ui/Layout";
import { api, rufe } from "../api/client";
import { formatEuro } from "@shared/geld";
import { formatDatumZeit, formatTag } from "@shared/zeit";
import { monatsName } from "@shared/abrechnung";
import { useMieterAbrechnungen } from "./api";
import { useMieterMahnungen } from "./konto-api";
import type { abrechnungDetail, Mietrechnung } from "../../worker/data/geld";

const STATUS: Record<string, { badge: "wait" | "paid" | "neutral" | "attention"; text: string }> = {
  entwurf: { badge: "neutral", text: "Entwurf" },
  erstellt: { badge: "wait", text: "Auszahlung folgt" },
  versendet: { badge: "wait", text: "Auszahlung folgt" },
  ausgezahlt: { badge: "paid", text: "Ausgezahlt" },
  uebertragen: { badge: "neutral", text: "Übertrag" },
  forderung: { badge: "attention", text: "Offener Betrag" },
  storniert: { badge: "neutral", text: "Storniert" },
};

const RABATT_TEXT: Record<string, string> = { maengel: "Mängel", mengenrabatt: "Mengenrabatt", kulanz: "Kulanz", aktion: "Aktion des Ladens", mieteraktion: "deine Aktion" };

export function Abrechnungen() {
  const tx = useTx();
  const q = useMieterAbrechnungen();
  const [tab, setTab] = useState<"auszahlungen" | "mietrechnungen">("auszahlungen");
  const rechnungen = useQuery({ queryKey: ["mieter", "mietrechnungen"], queryFn: () => rufe<{ rechnungen: Mietrechnung[] }>(api.api.mieter.mietrechnungen.$get()), enabled: tab === "mietrechnungen" });
  return (
    <Seitenzustand query={q} lines={5}>
      {(d) => (
        <div className="sc-col" style={{ gap: "var(--space-4)" }}>
          <h1 className="title">{tx("Abrechnungen")}</h1>
          <div className="sc-toolbar">
            <Link to="/mieter/konto" className="rg-btn rg-btn--secondary">Kontoauszug und Auszahlung</Link>
            <Link to="/mieter/rueckblick" className="rg-btn rg-btn--secondary">Monatsrückblick</Link>
          </div>
          <div className="sc-toolbar">
            <button type="button" className="sc-pill" aria-pressed={tab === "auszahlungen"} onClick={() => setTab("auszahlungen")}>Auszahlungen</button>
            <button type="button" className="sc-pill" aria-pressed={tab === "mietrechnungen"} onClick={() => setTab("mietrechnungen")}>Mietrechnungen</button>
          </div>
          {tab === "auszahlungen" ? (
            <>
              <Card title={d.laufend.von ? `${monatsName(d.laufend.von)} · läuft noch` : "Laufende Periode"} actions={<StatusBadge status="wait">Bisher</StatusBadge>}>
                <Dl
                  rows={[
                    ["Verkäufe", formatEuro(d.laufend.umsatzCent)],
                    ["Provision", formatEuro(d.laufend.provisionCent)],
                    ...(d.laufend.rabatteCent ? [["Rabatte", formatEuro(d.laufend.rabatteCent)] as [string, string]] : []),
                    ...(d.laufend.rueckgabenCent ? [["Rückgaben", formatEuro(d.laufend.rueckgabenCent)] as [string, string]] : []),
                    ...(d.laufend.mieteCent ? [["Miete", formatEuro(d.laufend.mieteCent)] as [string, string]] : []),
                    ["Bisher", <span key="s" className="sc-strong">{formatEuro(d.laufend.summeCent)}</span>],
                  ]}
                />
              </Card>
              {d.abrechnungen.length === 0 ? (
                <EmptyState compact illustration="all-done" title={tx("Noch keine Abrechnung")}>Nach der ersten abgeschlossenen Periode findest du hier jede Abrechnung mit Betrag, Auszahlungsweg und PDF.</EmptyState>
              ) : (
                <div>
                  {d.abrechnungen.map((a) => {
                    const s = STATUS[a.status] ?? { badge: "neutral" as const, text: a.status };
                    return <Link key={a.id} to={`/mieter/abrechnungen/${a.id}`} style={{ textDecoration: "none", color: "inherit" }}><ItemRow photo={false} title={`${a.art === "schluss" ? "Schlussabrechnung " : ""}${monatsName(a.periodeVon)}`} meta={`${formatTag(a.periodeVon)}–${formatTag(a.periodeBis)} · ${a.nummer}`} status={<StatusBadge status={s.badge}>{s.text}</StatusBadge>} price={a.auszahlungCent} /></Link>;
                  })}
                </div>
              )}
            </>
          ) : (
            <>
            <MahnungenHinweis />
            <Seitenzustand query={rechnungen} lines={3}>
              {(r) =>
                r.rechnungen.length === 0 ? (
                  <EmptyState compact illustration="all-done" title={tx("Noch keine Mietrechnung")}>Je Mietperiode entsteht eine Rechnung; sie wird mit deiner Abrechnung verrechnet.</EmptyState>
                ) : (
                  <div>
                    {r.rechnungen.map((x) => (
                      <ItemRow key={x.id} photo={false} title={x.nummer} meta={`${formatTag(x.leistungVon)}–${formatTag(x.leistungBis)} · ${x.status === "verrechnet" ? "mit Abrechnung verrechnet" : x.status === "bezahlt" ? "bezahlt" : x.status === "storniert" ? "storniert" : `wird verrechnet, sonst fällig ${formatTag(x.faelligAt)}`}`} price={x.bruttoCent} action={<a href={`/api/mieter/mietrechnungen/${x.id}/pdf`} target="_blank" rel="noreferrer" className="rg-btn rg-btn--quiet">PDF</a>} />
                    ))}
                  </div>
                )
              }
            </Seitenzustand>
            </>
          )}
        </div>
      )}
    </Seitenzustand>
  );
}

export function MieterAbrechnungDetail() {
  const tx = useTx();
  const { id = "" } = useParams();
  const q = useQuery({ queryKey: ["mieter", "abrechnung", id], queryFn: () => rufe<Awaited<ReturnType<typeof abrechnungDetail>>>(api.api.mieter.abrechnungen[":id"].$get({ param: { id } })), enabled: !!id });
  return (
    <Seitenzustand query={q} lines={6}>
      {(d) => {
        const a = d.abrechnung;
        const s = STATUS[a.status] ?? { badge: "neutral" as const, text: a.status };
        const summe = a.umsatzCent + a.rabatteCent + a.rueckgabenCent + a.provisionCent + a.mieteCent + a.uebertragCent + a.sonstigesCent;
        return (
          <div className="sc-col" style={{ gap: "var(--space-4)" }}>
            <div className="sc-row">
              <Link to="/mieter/abrechnungen" className="rg-btn rg-btn--quiet" aria-label="Zurück">‹</Link>
              <h1 className="title">{a.art === "schluss" ? "Schlussabrechnung" : "Abrechnung"} {monatsName(a.periodeVon)}</h1>
            </div>
            <StatusBadge status={s.badge}>{s.text}</StatusBadge>
            <Total amountCent={Math.abs(summe)} label={a.status === "forderung" ? "Offener Betrag an den Laden" : a.status === "uebertragen" ? "Übertrag in die nächste Periode" : "Auszahlung"} size="lg" caption={d.auszahlung ? `${d.auszahlung.art === "bar" ? "Bar ausgezahlt" : "Überwiesen"} am ${formatDatumZeit(d.auszahlung.ausgezahltAt)}` : d.mieter.auszahlungsweg === "bar" ? "Bar im Laden abholen" : `Per Überweisung${d.mieter.ibanMaske ? ` auf ${d.mieter.ibanMaske}` : " – bitte IBAN im Profil hinterlegen"}`} />
            <Card title={tx("Berechnung")}>
              <Dl
                rows={[
                  [`Verkäufe, ${d.verkaeufe.length} Artikel`, formatEuro(a.umsatzCent)],
                  ...(a.rabatteCent ? [["Rabatte", formatEuro(a.rabatteCent)] as [string, string]] : []),
                  ...(a.rueckgabenCent ? [["Rückgaben", formatEuro(a.rueckgabenCent)] as [string, string]] : []),
                  ["Provision", formatEuro(a.provisionCent)],
                  ...d.mietrechnungen.map((r) => [`Miete ${r.nummer}`, formatEuro(-r.bruttoCent)] as [string, string]),
                  ...(a.uebertragCent ? [["Übertrag", formatEuro(a.uebertragCent)] as [string, string]] : []),
                  ...(a.sonstigesCent ? [["Kaution / Sonstiges", formatEuro(a.sonstigesCent)] as [string, string]] : []),
                ]}
              />
              <a href={`/api/mieter/abrechnungen/${a.id}/pdf`} target="_blank" rel="noreferrer" className="rg-btn rg-btn--secondary" style={{ marginTop: "var(--space-3)" }}>Abrechnung als PDF</a>
            </Card>
            <h2 className="heading">{tx("Verkaufte Artikel")}</h2>
            <div>
              {d.verkaeufe.length === 0 ? <p className="sc-muted">{tx("Keine Verkäufe in dieser Periode.")}</p> : null}
              {d.verkaeufe.map((v) => <ItemRow key={v.id} photo={false} title={v.bezeichnung} meta={`${v.code ?? ""} · ${v.bezahltAt ? formatTag(v.bezahltAt.slice(0, 10)) : ""}${v.rabattCent ? ` · Rabatt ${formatEuro(-v.rabattCent)} (${RABATT_TEXT[v.rabattGrund ?? ""] ?? v.rabattGrund ?? ""})` : ""}`} price={v.endpreisCent} />)}
            </div>
          </div>
        );
      }}
    </Seitenzustand>
  );
}

/** Mahnungen (E9): offene Erinnerungen und Mahnungen mit PDF – ruhig benannt, nächster Schritt klar. */
function MahnungenHinweis() {
  const q = useMieterMahnungen();
  const offen = (q.data?.mahnungen ?? []).filter((m) => !m.stornierAt && m.rechnungStatus === "offen");
  if (!offen.length) return null;
  const STUFE = ["", "Zahlungserinnerung", "Mahnung", "Letzte Mahnung"];
  return (
    <Notice tone="attention" title={offen.length === 1 ? STUFE[offen[0]!.stufe] : `${offen.length} offene Mahnungen`}>
      {offen.map((m) => (
        <p key={m.id} style={{ margin: "4px 0" }}>
          Rechnung {m.rechnungNummer} über {formatEuro(m.bruttoCent)} ist seit {formatTag(m.faelligAt)} offen{m.gebuehrCent ? `, dazu ${formatEuro(m.gebuehrCent)} Mahngebühr` : ""}. <a href={`/api/mieter/mahnungen/${m.id}/pdf`} target="_blank" rel="noreferrer">Schreiben als PDF</a>
        </p>
      ))}
      <p style={{ margin: "4px 0 0" }}>Bitte überweise den Betrag oder sprich den Laden an. Bezahlt? Dann erledigt sich das beim nächsten Abgleich.</p>
    </Notice>
  );
}
